// =============================================================================
// FILE: packages/renderer/src/components/capture/MiniGeoMap.tsx
// PURPOSE: Dependency-free mini slippy-map. Renders OpenStreetMap raster tiles
//          with correct Web Mercator projection and overlays TWO markers — the
//          user's premises and the estimated serving switch/node — joined by a
//          glowing link line. No map library required.
// =============================================================================

import { useMemo, type JSX } from 'react';

interface Pt { lat: number; lng: number; }

function project(lat: number, lng: number, z: number): { x: number; y: number } {
  const s = 256 * Math.pow(2, z);
  const x = ((lng + 180) / 360) * s;
  const latRad = (lat * Math.PI) / 180;
  const y = ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * s;
  return { x, y };
}

export function MiniGeoMap({
  a, b, width = 544, height = 190, aLabel = 'YOU', bLabel = 'SWITCH',
}: { a: Pt; b: Pt; width?: number; height?: number; aLabel?: string; bLabel?: string }): JSX.Element {
  const m = useMemo(() => {
    const pad = 52;
    let zoom = 2;
    for (let z = 17; z >= 2; z--) {
      const pa = project(a.lat, a.lng, z), pb = project(b.lat, b.lng, z);
      if (Math.abs(pa.x - pb.x) <= width - 2 * pad && Math.abs(pa.y - pb.y) <= height - 2 * pad) { zoom = z; break; }
      zoom = z;
    }
    const c = project((a.lat + b.lat) / 2, (a.lng + b.lng) / 2, zoom);
    const originX = c.x - width / 2, originY = c.y - height / 2;
    const pa = project(a.lat, a.lng, zoom), pb = project(b.lat, b.lng, zoom);
    const A = { x: pa.x - originX, y: pa.y - originY };
    const B = { x: pb.x - originX, y: pb.y - originY };

    const n = Math.pow(2, zoom);
    const tiles: { x: number; y: number; left: number; top: number }[] = [];
    for (let tx = Math.floor(originX / 256); tx <= Math.floor((originX + width) / 256); tx++) {
      for (let ty = Math.floor(originY / 256); ty <= Math.floor((originY + height) / 256); ty++) {
        if (ty < 0 || ty >= n) continue;
        const wx = ((tx % n) + n) % n;
        tiles.push({ x: wx, y: ty, left: tx * 256 - originX, top: ty * 256 - originY });
      }
    }
    return { zoom, tiles, A, B };
  }, [a.lat, a.lng, b.lat, b.lng, width, height]);

  const Marker = ({ x, y, color, label, top }: { x: number; y: number; color: string; label: string; top?: boolean }): JSX.Element => (
    <div style={{ position: 'absolute', left: x, top: y, transform: 'translate(-50%,-50%)', pointerEvents: 'none' }}>
      <span style={{ position: 'absolute', inset: -8, borderRadius: '50%', border: `1px solid ${color}`, opacity: 0.5 }} className="animate-ping" />
      <span style={{ display: 'block', width: 12, height: 12, borderRadius: '50%', background: color, boxShadow: `0 0 8px ${color}`, border: '2px solid rgba(4,7,13,0.9)' }} />
      <span style={{ position: 'absolute', left: '50%', top: top ? undefined : '140%', bottom: top ? '140%' : undefined, transform: 'translateX(-50%)', whiteSpace: 'nowrap', fontSize: 8.5, fontWeight: 800, letterSpacing: '0.12em', color, textShadow: '0 1px 3px rgba(0,0,0,0.9)' }}>{label}</span>
    </div>
  );

  return (
    <div
      data-testid="last-mile-map"
      style={{ position: 'relative', width, maxWidth: '100%', height, margin: '0 auto', borderRadius: 12, overflow: 'hidden', border: '1px solid rgba(94,234,212,0.22)', background: '#0a1420' }}
    >
      {/* raster tiles (darkened to match theme) */}
      <div style={{ position: 'absolute', inset: 0, filter: 'invert(0.92) hue-rotate(180deg) saturate(0.75) brightness(0.92) contrast(0.95)' }}>
        {m.tiles.map((t) => (
          <img
            key={`${t.x}-${t.y}-${t.left}-${t.top}`}
            src={`https://tile.openstreetmap.org/${m.zoom}/${t.x}/${t.y}.png`}
            alt=""
            width={256}
            height={256}
            loading="lazy"
            style={{ position: 'absolute', left: t.left, top: t.top, width: 256, height: 256 }}
          />
        ))}
      </div>

      {/* subtle vignette */}
      <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at center, transparent 55%, rgba(4,7,13,0.55) 100%)', pointerEvents: 'none' }} />

      {/* link line between the two nodes */}
      <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
        <defs>
          <linearGradient id="mg-link" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor="#5eead4" /><stop offset="1" stopColor="#fcd34d" />
          </linearGradient>
        </defs>
        <line x1={m.A.x} y1={m.A.y} x2={m.B.x} y2={m.B.y} stroke="rgba(4,7,13,0.7)" strokeWidth="4" strokeLinecap="round" />
        <line x1={m.A.x} y1={m.A.y} x2={m.B.x} y2={m.B.y} stroke="url(#mg-link)" strokeWidth="2" strokeDasharray="5 4" strokeLinecap="round" style={{ filter: 'drop-shadow(0 0 4px rgba(94,234,212,0.7))' }} />
      </svg>

      <Marker x={m.A.x} y={m.A.y} color="#5eead4" label={aLabel} />
      <Marker x={m.B.x} y={m.B.y} color="#fcd34d" label={bLabel} top />
    </div>
  );
}
